import type { ActivityEvent } from '@/lib/apiClient';
import { fmtDateTime } from '@/lib/dates';

/**
 * Reusable, entity-agnostic activity timeline. Renders a vertical list of
 * who-did-what-when events. Self-contained (own <style>, only --ew-* tokens) so
 * any domain — submittals today, RFIs next — can render it unchanged:
 *
 *   <ActivityTimeline events={events} />
 *
 * `labels` overrides/extends the default action→label map; an unmapped action
 * falls back to its raw value so a new event type still renders sensibly.
 */

// Default action → human label. Submittal flow today; generic enough that RFIs
// can reuse most of it and override the rest via the `labels` prop.
const ACTION_LABELS: Record<string, string> = {
  'created': 'Created',
  'sent-to-reviewer': 'Sent to reviewer',
  'sent-to-assignee': 'Sent to assignee',
  'revision-sent': 'Revision sent',
  // #608 — a sent time corrected or cleared after the fact; detail is old → new.
  'sent-time-corrected': 'Sent time corrected',
  'marked-not-sent': 'Marked as not sent',
  'decision': 'Decision logged',
  'responded': 'Response logged',
  'reopened': 'Reopened',
  'ball-reassigned': 'Ball reassigned',
  'details-edited': 'Details edited',
  'file-uploaded': 'File uploaded',
  'file-removed': 'File removed',
};

const css = `
  .ew-tl { list-style: none; margin: 0; padding: 0; }
  .ew-tl-empty {
    font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font);
    color: var(--ew-n-400); padding: var(--ew-s-2) 0;
  }
  .ew-tl-item {
    position: relative; padding: 0 0 var(--ew-s-4) var(--ew-s-5);
  }
  .ew-tl-item:last-child { padding-bottom: 0; }
  /* The connector line runs down through the dot column. */
  .ew-tl-item::before {
    content: ''; position: absolute; left: 4px; top: 5px; bottom: 0;
    width: 2px; background: var(--ew-n-100);
  }
  .ew-tl-item:last-child::before { display: none; }
  .ew-tl-dot {
    position: absolute; left: 0; top: 4px; width: 10px; height: 10px;
    border-radius: 50%; background: var(--ew-quality); border: 2px solid #fff;
    box-shadow: 0 0 0 1px var(--ew-n-200);
  }
  .ew-tl-action {
    font: var(--ew-fw-semibold) var(--ew-fs-body-sm)/1.3 var(--ew-font);
    color: var(--ew-dark);
  }
  .ew-tl-detail {
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font);
    color: var(--ew-n-500); margin-top: 2px;
  }
  .ew-tl-meta {
    font: var(--ew-fw-regular) 12px/1.4 var(--ew-font);
    color: var(--ew-n-400); margin-top: 3px;
  }
`;

export interface ActivityTimelineProps {
  events: ActivityEvent[];
  labels?: Record<string, string>;
}

export default function ActivityTimeline({ events, labels }: ActivityTimelineProps) {
  if (!events || events.length === 0) {
    return (
      <>
        <style>{css}</style>
        <p className="ew-tl-empty">No activity yet.</p>
      </>
    );
  }

  const labelFor = (action: string) => labels?.[action] ?? ACTION_LABELS[action] ?? action;

  return (
    <>
      <style>{css}</style>
      <ul className="ew-tl">
        {events.map((e) => {
          const who = e.actor?.displayName ?? e.actor?.email ?? 'Unknown';
          return (
            <li key={e.id} className="ew-tl-item">
              <span className="ew-tl-dot" />
              <div className="ew-tl-action">{labelFor(e.action)}</div>
              {e.detail ? <div className="ew-tl-detail">{e.detail}</div> : null}
              <div className="ew-tl-meta">{who} · {fmtDateTime(e.createdAt)}</div>
            </li>
          );
        })}
      </ul>
    </>
  );
}
